if (!customElements.get('variant-selects')) {
  customElements.define(
    'variant-selects',
    class VariantSelects extends HTMLElement {
      constructor() {
        super();
      }

      get selectedOptionValues() {
        return Array.from(this.querySelectorAll('select option[selected], fieldset input:checked')).map(
          ({ dataset }) => dataset.optionValueId
        );
      }

      getInputForEventTarget(target) {
        return target.tagName === 'SELECT' ? target.selectedOptions[0] : target;
      }

      get productVariants() {
        if (this._productVariants) return this._productVariants;
        const el = this.querySelector('[data-product-variants]');
        this._productVariants = el ? JSON.parse(el.textContent) : [];
        return this._productVariants;
      }

      /**
       * Hide option values that have no matching variant given the currently
       * selected values for all *other* options.
       */
      filterOptionValues() {
        const variants = this.productVariants;
        if (!variants.length) return;

        // Collect all fieldsets/divs that represent an option, in DOM order
        const optionGroups = Array.from(
          this.querySelectorAll('.product-form__input')
        );

        // Build a map: groupIndex -> currently selected value label
        const selectedValues = optionGroups.map((group) => {
          const checked = group.querySelector('input:checked');
          if (checked) return checked.value;
          const sel = group.querySelector('select');
          if (sel) return sel.value;
          return null;
        });

        optionGroups.forEach((group, groupIndex) => {
          // For every input/option in this group, determine visibility
          const inputs = Array.from(group.querySelectorAll('input[type="radio"]'));
          const selectEl = group.querySelector('select');

          const checkValue = (valueLabel) => {
            // Does any variant match this value for this option AND all
            // selected values for options that come BEFORE this one?
            return variants.some((variant) => {
              const optionKey = `option${groupIndex + 1}`;
              if (variant[optionKey] !== valueLabel) return false;

              // Only filter based on preceding options (cascade pattern)
              for (let i = 0; i < groupIndex; i++) {
                if (selectedValues[i] === null) continue;
                const otherKey = `option${i + 1}`;
                if (variant[otherKey] !== selectedValues[i]) return false;
              }
              return true;
            });
          };

          if (inputs.length) {
            inputs.forEach((input) => {
              const label = input.nextElementSibling;
              const visible = checkValue(input.value);
              input.style.display = visible ? '' : 'none';
              if (label) label.style.display = visible ? '' : 'none';
            });
          } else if (selectEl) {
            Array.from(selectEl.options).forEach((opt) => {
              const visible = checkValue(opt.value);
              opt.hidden = !visible;
            });
          }
        });
      }

      /**
       * For groups after option 1, if the currently selected value was just
       * hidden by filterOptionValues(), click the first visible option so the
       * selection stays valid. Returns true if a click was dispatched.
       */
      autoSelectInvalidOptions() {
        const optionGroups = Array.from(this.querySelectorAll('.product-form__input'));

        for (let groupIndex = 1; groupIndex < optionGroups.length; groupIndex++) {
          const group = optionGroups[groupIndex];
          const inputs = Array.from(group.querySelectorAll('input[type="radio"]'));

          if (inputs.length) {
            const checkedInput = inputs.find((inp) => inp.checked);
            if (!checkedInput || checkedInput.style.display === 'none') {
              const firstVisible = inputs.find((inp) => inp.style.display !== 'none');
              if (firstVisible) {
                firstVisible.click();
                return true;
              }
            }
          } else {
            const selectEl = group.querySelector('select');
            if (selectEl) {
              const current = selectEl.options[selectEl.selectedIndex];
              if (current && current.hidden) {
                const firstVisible = Array.from(selectEl.options).find((o) => !o.hidden);
                if (firstVisible) {
                  selectEl.value = firstVisible.value;
                  selectEl.dispatchEvent(new Event('change', { bubbles: true }));
                  return true;
                }
              }
            }
          }
        }
        return false;
      }

      connectedCallback() {
        if (this._hasChangeListener) {
          return;
        }

        this._hasChangeListener = true;

        // Run once on load to hide values incompatible with the initial selection
        this.filterOptionValues();
        this.autoSelectInvalidOptions();

        this.addEventListener(
          'change',
          (event) => {
            // Check if target is inside this element
            const targetInThisElement = this.contains(event.target);

            // Check if there's a nested variant-selects between this and target
            let hasNestedVariantSelects = false;
            let currentElement = event.target;

            while (currentElement && currentElement !== document.body) {
              if (currentElement.tagName === 'VARIANT-SELECTS') {
                if (currentElement !== this) {
                  hasNestedVariantSelects = true;
                  break;
                } else {
                  break;
                }
              }
              currentElement = currentElement.parentElement;
            }

            // Ignore if target is not in this element or there's a nested variant-selects
            if (!targetInThisElement || hasNestedVariantSelects) {
              return;
            }

            const target = this.getInputForEventTarget(event.target);
            this.updateSelectedSwatchValue(event);
            this.filterOptionValues();
            // If an invalid downstream option was auto-corrected, that click will
            // fire its own change event which will publish the final state.
            if (this.autoSelectInvalidOptions()) return;
            FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.optionValueSelectionChange, {
              data: {
                event,
                target,
                selectedOptionValues: this.selectedOptionValues,
              },
            });
          },
          true
        ); // Use CAPTURE phase to catch events before they bubble
      }

      updateSelectedSwatchValue({ target }) {
        const { value, tagName } = target;

        if (tagName === 'SELECT' && target.selectedOptions.length) {
          Array.from(target.options)
            .find((option) => option.getAttribute('selected'))
            .removeAttribute('selected');
          target.selectedOptions[0].setAttribute('selected', 'selected');

          const swatchValue = target.selectedOptions[0].dataset.optionSwatchValue;
          const selectedDropdownSwatchValue = target
            .closest('.product-form__input')
            .querySelector('[data-selected-value] > .swatch');
          if (!selectedDropdownSwatchValue) return;
          if (swatchValue) {
            selectedDropdownSwatchValue.style.setProperty('--swatch--background', swatchValue);
            selectedDropdownSwatchValue.classList.remove('swatch--unavailable');
          } else {
            selectedDropdownSwatchValue.style.setProperty('--swatch--background', 'unset');
            selectedDropdownSwatchValue.classList.add('swatch--unavailable');
          }

          selectedDropdownSwatchValue.style.setProperty(
            '--swatch-focal-point',
            target.selectedOptions[0].dataset.optionSwatchFocalPoint || 'unset'
          );
        } else if (tagName === 'INPUT' && target.type === 'radio') {
          const selectedSwatchValue = target.closest(`.product-form__input`).querySelector('[data-selected-value]');
          if (selectedSwatchValue) selectedSwatchValue.innerHTML = value;
        }
      }
    }
  );
}
